import type { PortfolioReport } from '@interfaces/portfolioReport';
import type { SubAccountReport } from '@interfaces/subAccountReport';
import { CurrencyCodes } from '@onevesthq/ov-enums';

export const resolvePortfolioBaseCurrency = (
  report?: Pick<PortfolioReport, 'baseCurrency'> | null,
): CurrencyCodes => report?.baseCurrency ?? CurrencyCodes.CAD;

const addCurrency = (
  currencies: Set<CurrencyCodes>,
  value?: CurrencyCodes | null,
) => {
  if (value) {
    currencies.add(value);
  }
};

/** Distinct currencies on a portfolio report for flag display. */
export const resolvePortfolioDisplayCurrencies = (
  report?: PortfolioReport | null,
): CurrencyCodes[] => {
  if (!report) {
    return [CurrencyCodes.CAD];
  }

  const currencies = new Set<CurrencyCodes>();
  addCurrency(currencies, resolvePortfolioBaseCurrency(report));

  for (const currency of report.originalCurrencies ?? []) {
    addCurrency(currencies, currency);
  }

  for (const subAccountReport of report.subAccounts ?? []) {
    addSubAccountCurrencies(currencies, subAccountReport);
  }

  return [...currencies];
};

const addSubAccountCurrencies = (
  currencies: Set<CurrencyCodes>,
  subAccountReport: SubAccountReport,
) => {
  addCurrency(currencies, subAccountReport.currency);
  addCurrency(currencies, subAccountReport.accountBaseCurrency);

  for (const originalValue of subAccountReport.originalValues ?? []) {
    addCurrency(currencies, originalValue.currency);
  }
};

export const isMultiCurrencyPortfolio = (
  report?: PortfolioReport | null,
): boolean => resolvePortfolioDisplayCurrencies(report).length > 1;

/** Joined ISO codes for multi-currency portfolios, e.g. "CAD · USD". */
export const formatPortfolioCurrencyLabel = (
  report?: PortfolioReport | null,
): string | undefined => {
  if (!isMultiCurrencyPortfolio(report)) {
    return undefined;
  }

  return resolvePortfolioDisplayCurrencies(report).join(' · ');
};
